import { useSelector } from "react-redux";
import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import { CompactModuleFilter } from "../../../utils/RelatedEntries/CompactModuleFilter";
import { useRelatedCollection } from "../../../utils/RelatedEntries/useRelatedCollection";
import { RelatedEntries } from "../../../utils/RelatedEntries/RelatedEntries";
import {
  RightDrawer,
  DrawerProps,
  DrawerSection,
} from "../../../utils/PMTableView/RightDrawer";
import {
  mergeScopedFilters,
  useScopeFiltersSync,
  computeHasMore,
  embeddedFetchKey,
  scopeFiltersKey,
} from "../../../utils/PMTableView/embedded-table-utils";
import { PeriodSelector } from "../../hours/period_selector";
import {
  fmtDate,
  reportAppointmentDate,
} from "./reports-table";
import {
  reportStatusesOptions,
  reportViewsOptions,
  ReportStatus,
  FullReportAddress,
  ReportAppointmentApprovedStatus,
} from "./utils";
import { getReportsAsync } from "../../../../slices/service/reports";
import ReportOverviewSummary from "./ReportOverviewSummary";
import { ReportsProjectExportDialog } from "./reports-project-export-dialog";
import { PMDialog } from "../../../utils/PMDialog";
import { PromangRichTextContent } from "../../../utils/PromangRichTextContent";

const DEFAULT_REPORT_FIELDS =
  "id,report_number,customer.id,customer.name,customer.customer_number,project,delivery_address,status,address,postal,place,appointment,report_type,description,created_at";

export const ReportsStackList = (props) => {
  const companyId = useSelector((state) => state.session.currentCompany?.id);
  return <ReportsStackListContent key={JSON.stringify([companyId, props.module, props.filters])} {...props} />;
};

const ReportsStackListContent = (props) => {
  const {
    filters: scopeFilters = [],
    module: moduleName = "embedded",
    showDeleted = false,
    project = null,
  } = props;

  const { t } = useTranslation();
  const navigate = useNavigate();

  const [itemState, loadItems] = useRelatedCollection(getReportsAsync, JSON.stringify(scopeFilters));
  const { isLoading, count: totalCount } = itemState;

  const loadMoreInFlightRef = useRef(false);
  const [drawerReport, setDrawerReport] = useState(null);
  const [descriptionReport, setDescriptionReport] = useState(null);
  const [reportOverviewOpen, setReportOverviewOpen] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState(null);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);

  const [period, setPeriod] = useState({ periodType: "month", from: null, to: null });
  const dateField = "all";

  const [controller, setController] = useState(() => ({
    filters: mergeScopedFilters(scopeFilters, []),
    showDeleted,
    page: 1,
    per_page: props.per_page ? parseInt(props.per_page, 10) : 25,
    query: "",
    sort: "desc",
    sortBy: "created_at",
    view: "all",
    fields: [...new Set(`${props.fields || DEFAULT_REPORT_FIELDS},created_at,project`.split(",").map((field) => field.trim()))].join(","),
    date_from: null,
    date_to: null,
    date_field: "all",
  }));

  useScopeFiltersSync(scopeFilters, setController);

  useEffect(() => {
    setController((c) => {
      if (c.showDeleted === showDeleted) return c;
      return { ...c, page: 1, showDeleted };
    });
  }, [showDeleted]);

  const handleFiltersChange = (filters) => {
    const userFilters = [];
    if (filters.statuses?.length > 0) {
      userFilters.push({
        name: "status",
        value: JSON.stringify(filters.statuses),
      });
    }
    if (filters.views?.length > 0) {
      userFilters.push({
        name: "view",
        value: JSON.stringify(filters.views),
      });
    }
    setController((c) => ({
      ...c,
      page: 1,
      query: filters.query || "",
      filters: mergeScopedFilters(scopeFilters, userFilters),
    }));
  };

  const getInitialFilters = () => {
    const initial = {
      query: controller.query || "",
      statuses: [],
      views: [],
    };
    controller.filters.forEach((filter) => {
      if ((scopeFilters || []).some((sf) => sf.name === filter.name)) return;
      try {
        const values = JSON.parse(filter.value);
        if (filter.name === "status") initial.statuses = values;
        if (filter.name === "view") initial.views = values;
      } catch {
        // ignore
      }
    });
    return initial;
  };

  const handlePeriodChange = (newPeriod) => {
    if (!newPeriod) return;
    setPeriod(newPeriod);
    setController((c) => ({
      ...c,
      page: 1,
      date_from: newPeriod.from || null,
      date_to: newPeriod.to || null,
      date_field: dateField,
    }));
  };

  const handleLoadMore = useCallback(() => {
    const loaded = itemState?.data?.length || 0;
    const total = totalCount || 0;
    if (loadMoreInFlightRef.current || isLoading || loaded >= total) return;

    const nextPage = (controller.page || 1) + 1;
    loadMoreInFlightRef.current = true;
    loadItems({ ...controller, page: nextPage, append: true }).then((loaded) => { if (loaded) setController((c) => ({ ...c, page: nextPage })); }).finally(
      () => {
        loadMoreInFlightRef.current = false;
      }
    );
  }, [
    controller,
    loadItems,
    isLoading,
    itemState?.data?.length,
    totalCount,
  ]);

  const debouncedFetch = useMemo(
    () => debounce((ctrl) => loadItems(ctrl), 250),
    [loadItems]
  );

  const filtersKey = scopeFiltersKey(controller.filters);
  const fetchKey = useMemo(
    () =>
      embeddedFetchKey(controller, {
        showDeleted: controller.showDeleted,
        date_from: controller.date_from,
        date_to: controller.date_to,
        date_field: controller.date_field,
      }),
    [
      filtersKey,
      controller.query,
      controller.sort,
      controller.sortBy,
      controller.per_page,
      controller.view,
      controller.fields,
      controller.showDeleted,
      controller.date_from,
      controller.date_to,
      controller.date_field,
    ]
  );

  useEffect(() => {
    setController((c) => (c.page === 1 ? c : { ...c, page: 1 }));
    debouncedFetch({ ...controller, page: 1 });
    return () => debouncedFetch.cancel();
  }, [fetchKey, debouncedFetch]); // eslint-disable-line react-hooks/exhaustive-deps

  const dataLength = itemState?.data?.length || 0;
  const hasMore = computeHasMore({ isLoading, dataLength, totalCount });

  const statusOptions = reportStatusesOptions.map((option) => ({
    value: option.value,
    label: option.label,
    class: "bg-blue-100 text-blue-800",
  }));
  const viewOptions = reportViewsOptions.map((option) => ({
    value: option.value,
    label: option.label,
    class: "bg-purple-100 text-purple-800",
  }));

  return (
    <>
      <div className="min-w-0 space-y-4">
        {project?.id ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <PeriodSelector
              periodType={period.periodType}
              from={period.from}
              to={period.to}
              onChange={handlePeriodChange}
            />
            <button
              type="button"
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm hover:bg-slate-50"
              onClick={() => setExportDialogOpen(true)}
            >
              {t("Export")}
            </button>
          </div>
        ) : null}

        <CompactModuleFilter
          disabled={isLoading}
          onFiltersChange={handleFiltersChange}
          initialFilters={getInitialFilters()}
          searchPlaceholder={t("report_list.search_placeholder")}
          filterTitle={t("module_overview.filter_reports")}
          statusOptions={statusOptions}
          viewOptions={viewOptions}
          customDropdownFilters={[
            {
              key: "views",
              label: t("Views"),
              options: viewOptions,
            },
          ]}
        />

        <div className="min-w-0">
          <RelatedEntries kind="reports" context={moduleName} data={itemState.data} isLoading={isLoading} error={itemState.error} totalCount={totalCount}
          onRetry={() => { setController((value) => ({ ...value, page: 1 })); loadItems({ ...controller, page: 1 }); }}
          onRowClick={setDrawerReport}
          onDescriptionClick={setDescriptionReport}
          renderStatus={(item) => <div className="flex flex-wrap items-center gap-2"><ReportStatus report={item} /><ReportAppointmentApprovedStatus report={item} type="short" /></div>} hasMore={hasMore} onLoadMore={handleLoadMore}
        />
        </div>
      </div>

      <PMDialog
        open={!!descriptionReport}
        onClose={() => setDescriptionReport(null)}
        size="2xl"
        eyebrow={
          descriptionReport
            ? t("relatedEntries.descriptionDialogEyebrow", {
                number: descriptionReport.report_number ?? descriptionReport.id,
              })
            : undefined
        }
        title={t("relatedEntries.descriptionDialogTitle")}
        subtitle={t("report.description")}
      >
        <div className="pm-rich-preview max-h-[min(70vh,32rem)] overflow-y-auto">
          <PromangRichTextContent
            html={descriptionReport?.description || ""}
            empty={
              <p className="text-sm text-slate-500">
                {t("report.redesign.no_description")}
              </p>
            }
          />
        </div>
        <div className="pm-form-footer mt-6">
          <button
            type="button"
            className="pm-btn pm-btn--secondary"
            onClick={() => setDescriptionReport(null)}
          >
            {t("Close")}
          </button>
        </div>
      </PMDialog>

      <RightDrawer
        open={!!drawerReport}
        onClose={() => setDrawerReport(null)}
        eyebrow={drawerReport ? `${t("Report")} #${drawerReport.report_number}` : ""}
        title={
          drawerReport?.customer
            ? `${drawerReport.customer.customer_number || ""} ${drawerReport.customer.name || ""}`.trim()
            : t("Report")
        }
        actions={[
          {
            label: t("Overview"),
            onClick: () => {
              setSelectedReportId(drawerReport.id);
              setReportOverviewOpen(true);
              setDrawerReport(null);
            },
          },
          {
            label: t("Open"),
            icon: ArrowTopRightOnSquareIcon,
            primary: true,
            onClick: () => navigate(`/dashboard/service/reports/${drawerReport.id}`),
          },
        ]}
      >
        {drawerReport && (
          <>
            <DrawerSection title={t("Report")}>
              <DrawerProps
                items={[
                  { label: t("Status"), value: <ReportStatus report={drawerReport} /> },
                  {
                    label: t("Type"),
                    value: drawerReport.report_type?.title || "—",
                  },
                  {
                    label: t("Customer"),
                    value: drawerReport.customer
                      ? `${drawerReport.customer.customer_number || ""} ${drawerReport.customer.name || ""}`.trim() || "—"
                      : "—",
                  },
                  {
                    label: t("Address"),
                    value: FullReportAddress(drawerReport) || "—",
                  },
                  {
                    label: t("Created"),
                    value: fmtDate(drawerReport.created_at),
                  },
                ]}
              />
            </DrawerSection>
            <DrawerSection title={t("report.list.agreed_date")}>
              <DrawerProps
                items={[
                  {
                    label: t("report.list.agreed_date"),
                    value: fmtDate(reportAppointmentDate(drawerReport)),
                  },
                  {
                    label: t("Status"),
                    value: drawerReport.appointment ? (
                      <ReportAppointmentApprovedStatus report={drawerReport} />
                    ) : (
                      t("Ikke avtalt")
                    ),
                  },
                ]}
              />
            </DrawerSection>
          </>
        )}
      </RightDrawer>

      {project?.id ? (
        <ReportsProjectExportDialog
          open={exportDialogOpen}
          onClose={() => setExportDialogOpen(false)}
          project={project}
          controller={controller}
          period={period}
        />
      ) : null}

      <ReportOverviewSummary
        open={reportOverviewOpen}
        onClose={() => {
          setReportOverviewOpen(false);
          setSelectedReportId(null);
        }}
        reportId={selectedReportId}
      />
    </>
  );
};

ReportsStackList.defaultProps = {
  filters: [],
  sort: "desc",
  sortBy: "created_at",
  showDeleted: false,
};
